import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecurityAssessment = () => {
  const assessmentItems = [
    {
      id: 1,
      title: 'Two-Factor Authentication',
      description: 'Add an extra layer of security to your account',
      status: 'completed',
      icon: 'ShieldCheck',
      priority: 'high',
      points: 25
    },
    {
      id: 2,
      title: 'Strong Password',
      description: 'Use a password with at least 12 characters',
      status: 'completed',
      icon: 'Key',
      priority: 'high',
      points: 20
    },
    {
      id: 3,
      title: 'Trusted Devices',
      description: 'Mark your regular devices as trusted',
      status: 'completed',
      icon: 'Laptop',
      priority: 'medium',
      points: 15
    },
    {
      id: 4,
      title: 'Email Verification',
      description: 'Verify your email address for account recovery',
      status: 'completed',
      icon: 'Mail',
      priority: 'high',
      points: 20
    },
    {
      id: 5,
      title: 'Backup Codes',
      description: 'Save backup codes for emergency access',
      status: 'pending',
      icon: 'FileKey',
      priority: 'medium',
      points: 10
    },
    {
      id: 6,
      title: 'Login Notifications',
      description: 'Get notified of new login attempts',
      status: 'pending',
      icon: 'Bell',
      priority: 'low',
      points: 10
    }
  ];

  const completedItems = assessmentItems?.filter(item => item?.status === 'completed');
  const totalPoints = assessmentItems?.reduce((sum, item) => sum + item?.points, 0);
  const earnedPoints = completedItems?.reduce((sum, item) => sum + item?.points, 0);
  const completionPercentage = Math.round((earnedPoints / totalPoints) * 100);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    return status === 'completed' ? 'CheckCircle' : 'Circle';
  };

  const getStatusColor = (status) => {
    return status === 'completed' ? 'text-success' : 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-4 md:p-6 lg:p-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="ClipboardCheck" size={24} color="var(--color-success)" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">Security Assessment</h2>
          <p className="text-sm text-muted-foreground">Complete these steps to maximize your account security</p>
        </div>
      </div>
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex-1 text-center lg:text-left">
            <div className="text-sm text-muted-foreground mb-2">Overall Security Score</div>
            <div className="text-4xl md:text-5xl font-bold text-foreground mb-2">{completionPercentage}%</div>
            <div className="text-sm text-muted-foreground">
              {completedItems?.length} of {assessmentItems?.length} items completed
            </div>
          </div>
          <div className="w-full lg:w-64">
            <div className="relative w-32 h-32 md:w-40 md:h-40 mx-auto">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  fill="none"
                  stroke="var(--color-muted)"
                  strokeWidth="8"
                />
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  fill="none"
                  stroke="var(--color-success)"
                  strokeWidth="8"
                  strokeDasharray={`${completionPercentage * 2.83} 283`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon name="Shield" size={48} color="var(--color-success)" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-3 mb-6">
        {assessmentItems?.map((item) => (
          <div key={item?.id} className={`border rounded-lg p-4 transition-all ${item?.status === 'completed' ? 'border-success/30 bg-success/5' : 'border-border hover:border-primary/50'}`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${item?.status === 'completed' ? 'bg-success/10' : 'bg-muted'}`}>
                <Icon name={item?.icon} size={20} color={item?.status === 'completed' ? 'var(--color-success)' : 'var(--color-muted-foreground)'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-medium text-foreground mb-1">{item?.title}</h3>
                    <p className="text-sm text-muted-foreground">{item?.description}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-medium ${getPriorityColor(item?.priority)} uppercase`}>
                      {item?.priority}
                    </span>
                    <Icon name={getStatusIcon(item?.status)} size={20} className={getStatusColor(item?.status)} />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-2">
                    <Icon name="Award" size={14} color="var(--color-accent)" />
                    <span className="text-xs text-muted-foreground">+{item?.points} points</span>
                  </div>
                  {item?.status === 'pending' && (
                    <Button variant="outline" size="sm">
                      Complete
                    </Button>
                  )}
                  {item?.status === 'completed' && (
                    <span className="text-xs text-success font-medium">Completed</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
        <Icon name="Lightbulb" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground mb-1">Security Recommendation</h3>
          <p className="text-sm text-muted-foreground">
            Complete the remaining security steps to achieve a 100% security score. Enable backup codes and login notifications to ensure you never lose access to your account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SecurityAssessment;